import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';
import { ROUTE_THING_EDITBUCKETS } from '../../constants';
import SelectBox from '../../components/SelectBox';
import CachedIcon from '@mui/icons-material/Cached';
import IconButtonBox from '../../components/IconButtonBox';
import OutlinedButton from '../../components/OutlinedButton';
import ThingApi from '../../api/ThingApi';
import useThingStore from '../../stores/ThingStore';
import { Option } from "../../types/OptionType";
import ButtonBox from '../../components/ButtonBox';
import NewTableBox from '../../components/NewTableBox';
import { useError } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';

const ThingData: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();

  const { thingObj } = useThingStore();
  const [options, setOptions] = useState<Option[]>([]);
  const [data, setData] = useState([]);

  const [totalCount, setTotalCount] = useState(0);
  const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

  const columns = [
    { field: "bucketData", title: "BucketData" },
    { field: "reportTime", title: "Report Time" },
  ];

  const [formValues, setFormValues] = useState({
    name: '',
    order: 'desc',
    begin: '',
    end: '',
  });

  const setValue = (field: string, value: any) => {
    setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
  };

  const handleQuery = async () => {
    setData([]);
    try {
      if (thingObj.id && formValues.name && formValues.begin && formValues.end) {
        const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();
        const paginationParams = {
          limit: limit,
          beforeCursor: beforeCursor,
          afterCursor: afterCursor,
          order: formValues.order
        };

        const res = await ThingApi.getConfigReportRecords(thingObj.id, formValues.name, formValues.begin, formValues.end, paginationParams);
        if (res.code) {
          setError(res.message);
        } else {
          const parsedData = res.data.map((obj: any) => ({
            ...obj,
            bucketData: JSON.stringify(obj.bucketData, null, 2),
          }));

          setData(parsedData);

          if (res.metadata && res.metadata.total) {
            setTotalCount(res.metadata.total);
          }

          if (res.paging) {
            setAfterCursor(res.paging.afterCursor);
            setBeforeCursor(res.paging.beforeCursor);
          }
        }
      }
    } catch (error) {
      console.error("Error handleQuery data:", error);
    } finally {
      closeAction();
    }
  };

  const getListDataBuckets = async () => {
    setOptions([]);
    if (thingObj.id) {
      const res = await ThingApi.listDataBuckets(thingObj.id);

      if (res.code) {
        setError(res.message);
      } else {
        const newOptions = Object.values(res).map((obj: any) => ({
          value: obj.id,
          label: obj.id
        }));

        if (newOptions && newOptions.length > 0) {
          setValue('name', newOptions[0].value);
        }

        setOptions(newOptions);
      }
    }

  };

  useEffect(() => {
    resetStore();
    getListDataBuckets();
  }, []);

  useEffect(() => {
    if (pageAction) {
      handleQuery();
    }
  }, [pageAction]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mb={4}>
          <Typography variant="h4" gutterBottom className='primaryBlueColor'>Data</Typography>
        </Box>
        <Box>
          <OutlinedButton
            onClick={() => navigate(ROUTE_THING_EDITBUCKETS)}
            icon={<EditOutlinedIcon />}
            text='Edit buckets' />
        </Box>
      </Box>

      <Box mb={4} display="flex" alignItems="center" gap={4}>
        {/* Bucket SelectBox */}
        <Box flex={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 2 }}>
            <Typography sx={{ width: '100px' }}>Bucket</Typography>
            <SelectBox
              label=""
              name="bucket"
              value={formValues.name}
              options={options}
              className='w150'
              onChange={(e) => setValue('name', e.target.value)}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 2 }}>
            <Typography sx={{ width: '100px' }}>Order</Typography>
            <SelectBox
              label=""
              name="order"
              value={formValues.order}
              options={[{ value: 'desc', label: 'Desc' }, { value: 'asc', label: 'Asc' }]}
              className='w150'
              onChange={(e) => setValue('order', e.target.value)}
            />

            <Box className='w50' />

            <Typography>Time</Typography>
            <TextField
              type="datetime-local"
              value={formValues.begin}
              size="small"
              className='w250'
              onChange={(e) => setValue('begin', e.target.value)}
            />
            <TextField
              type="datetime-local"
              value={formValues.end}
              size="small"
              className='w250'
              onChange={(e) => setValue('end', e.target.value)}
            />

            <ButtonBox
              text="Search"
              onClick={() => { setReQuery(true); handleQuery(); }}
            />
          </Box>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box />
        <Box>
          <IconButtonBox
            icon={<CachedIcon />}
            text='Refresh'
            onClick={handleQuery} />
        </Box>
      </Box>

      <NewTableBox columns={columns} data={data} totalCount={totalCount} />

    </Box>
  );
};

export default ThingData;
